/**
 * PromptViewerModal — "View AI prompt" on the eligibility results page.
 *
 * Fetches GET /api/crm/eligibility-reviews/:id/prompts and shows, for each of
 * the two prompts the facts-engine pipeline uses (the Tier-1 and Tier-2
 * judges), which version actually RAN for this review vs the CURRENT
 * registry version — with an explicit warning when they differ. The full
 * current prompt text renders in a scrollable pre-style block; CRM admins
 * get an "Edit in AI Prompts" link (the registry at /crm/settings/prompts).
 *
 * Historical reviews from the retired legacy engine recorded a DIFFERENT
 * pair of prompts (review_system/criterion_analysis) that has no "current"
 * counterpart anymore — for those, an info banner shows the legacy versions
 * verbatim instead of the usual changed-prompt warning (the keys don't
 * overlap, so a version/source diff against the facts prompts would be
 * meaningless).
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, Info, Loader2, ScrollText, X } from 'lucide-react';
import api from '../../../services/api';
import {
  EligibilityPromptCurrent,
  EligibilityPromptVersionRef,
  EligibilityReviewPromptsResponse,
} from '../../types';
import { useIsCRMAdmin, dateTime } from '../../lib';

const errMsg = (e: any): string =>
  e?.response?.data?.detail || e?.response?.data?.error || e?.message || 'Request failed';

type PromptKey = 'tier1_judge' | 'tier2_judge';

const PROMPT_TABS: { key: PromptKey; label: string; description: string }[] = [
  {
    key: 'tier1_judge',
    label: 'Tier-1 judge',
    description: 'The cheap-model (Haiku) first pass: one criterion plus a small block of retrieved facts and page snippets, returning a findings JSON.',
  },
  {
    key: 'tier2_judge',
    label: 'Tier-2 judge',
    description: 'The escalation pass (Sonnet), used only when Tier-1 is uncertain or conflicted — same findings contract, with fuller retrieved page context.',
  },
];

/** True when `ran` is a legacy-era prompt_meta (review_system/criterion_analysis
 *  keys) rather than the current facts-engine shape (tier1_judge/tier2_judge). */
function isLegacyRan(ran: EligibilityReviewPromptsResponse['ran'] | null | undefined): boolean {
  return !!ran && (ran.review_system != null || ran.criterion_analysis != null)
    && ran.tier1_judge == null && ran.tier2_judge == null;
}

/** "v3 (admin-edited)" / "v1 (code default)" from a version ref. */
function versionLabel(ref: EligibilityPromptVersionRef | EligibilityPromptCurrent | null | undefined): string {
  if (!ref) return 'unknown';
  const v = ref.version != null ? `v${ref.version}` : 'unversioned';
  const src = ref.source === 'db' ? 'admin-edited' : ref.source === 'code' ? 'code default' : ref.source;
  return src ? `${v} (${src})` : v;
}

interface Props {
  reviewId: string;
  onClose: () => void;
}

export default function PromptViewerModal({ reviewId, onClose }: Props) {
  const isAdmin = useIsCRMAdmin();
  const [data, setData] = useState<EligibilityReviewPromptsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<PromptKey>('tier1_judge');

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(null);
    api.get(`/api/crm/eligibility-reviews/${reviewId}/prompts`)
      .then((res) => { if (alive) setData(res.data || null); })
      .catch((e) => { if (alive) setError(errMsg(e)); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [reviewId]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const current = data?.current?.[tab] || null;
  const legacyRan = isLegacyRan(data?.ran);
  // Legacy-era prompt_meta has no tier1_judge/tier2_judge key at all — comparing
  // against it would silently read undefined, so only look up ranRef in the
  // (facts-era-or-null) case; the legacy case renders its own info banner instead.
  const ranRef = data?.ran && !legacyRan ? data.ran[tab] : null;
  const changed =
    !!current && !!ranRef &&
    (current.version !== ranRef.version || (current.source || null) !== (ranRef.source || null));
  const activeMeta = PROMPT_TABS.find((t) => t.key === tab);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="flex max-h-[90vh] w-full max-w-3xl flex-col overflow-hidden rounded-xl bg-white shadow-xl dark:bg-gray-800"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="AI prompts used by this review"
      >
        <div className="flex items-start justify-between gap-3 border-b border-gray-200 p-4 dark:border-gray-700">
          <div className="min-w-0">
            <h2 className="inline-flex items-center gap-2 text-base font-semibold text-gray-900 dark:text-gray-100">
              <ScrollText className="h-4 w-4 text-primary-600 dark:text-primary-400" />
              AI prompts for this review
            </h2>
            {data?.ran && (
              <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                {[
                  data.ran.model_id ? `Ran on ${data.ran.model_id}` : null,
                  data.ran.recorded_at ? `recorded ${dateTime(data.ran.recorded_at)}` : null,
                ].filter(Boolean).join(' · ')}
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close prompt viewer"
            className="inline-flex min-h-[44px] min-w-[44px] shrink-0 items-center justify-center rounded-lg text-gray-500 transition-colors duration-150 hover:bg-gray-100 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-gray-200 sm:min-h-0 sm:min-w-0 sm:p-1.5"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex flex-wrap gap-1.5 border-b border-gray-100 px-4 py-2.5 dark:border-gray-700">
          {PROMPT_TABS.map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => setTab(t.key)}
              className={`inline-flex min-h-[44px] items-center rounded-full px-3 py-1.5 text-xs font-medium transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-primary-500 sm:min-h-0 ${
                tab === t.key
                  ? 'bg-primary-600 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="flex items-center justify-center py-10">
              <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
            </div>
          ) : error ? (
            <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400">
              {error}
            </div>
          ) : current ? (
            <>
              {activeMeta && (
                <p className="text-xs text-gray-500 dark:text-gray-400">{activeMeta.description}</p>
              )}
              <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
                <span className="text-gray-600 dark:text-gray-300">
                  <span className="font-semibold">Ran with:</span>{' '}
                  {legacyRan ? 'retired legacy engine' : data?.ran ? versionLabel(ranRef) : 'not recorded for this run'}
                </span>
                <span className="text-gray-600 dark:text-gray-300">
                  <span className="font-semibold">Current:</span> {versionLabel(current)}
                </span>
                {current.max_tokens != null && (
                  <span className="text-gray-400">max tokens {current.max_tokens}</span>
                )}
              </div>
              {legacyRan ? (
                <div
                  role="status"
                  className="mt-2 flex items-start gap-2 rounded-lg border border-primary-200 bg-primary-50 p-2.5 text-xs text-primary-800 dark:border-primary-800 dark:bg-primary-900/20 dark:text-primary-300"
                >
                  <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                  <span>
                    This review ran on the retired legacy engine — its recorded prompt versions were{' '}
                    <span className="font-semibold">
                      reviewer system {versionLabel(data?.ran?.review_system)} · per-criterion{' '}
                      {versionLabel(data?.ran?.criterion_analysis)}
                    </span>
                    . The text below is the CURRENT facts-engine {activeMeta?.label.toLowerCase()} prompt, not
                    what produced these results.
                  </span>
                </div>
              ) : changed && (
                <div
                  role="alert"
                  className="mt-2 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-2.5 text-xs text-amber-800 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-300"
                >
                  <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                  <span>
                    This prompt has changed since the review ran — the text below is the CURRENT
                    version, not necessarily what produced these results.
                  </span>
                </div>
              )}
              <pre className="mt-3 max-h-[45vh] overflow-y-auto whitespace-pre-wrap break-words rounded-lg border border-gray-200 bg-gray-50 p-3 font-mono text-xs leading-relaxed text-gray-700 dark:border-gray-700 dark:bg-gray-900/60 dark:text-gray-300">
                {current.text || '(prompt text unavailable)'}
              </pre>
            </>
          ) : (
            <p className="text-sm text-gray-500 dark:text-gray-400">Prompt information is unavailable.</p>
          )}
        </div>

        <div className="flex items-center justify-between gap-2 border-t border-gray-100 p-4 dark:border-gray-700">
          {isAdmin ? (
            <Link
              to={data?.editable_in || '/crm/settings/prompts'}
              className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg border border-primary-200 bg-white px-3 py-1.5 text-xs font-medium text-primary-700 transition-colors duration-150 hover:bg-primary-50 dark:border-primary-800 dark:bg-gray-800 dark:text-primary-300 dark:hover:bg-primary-900/20 sm:min-h-0"
            >
              <ScrollText size={13} />
              Edit in AI Prompts
            </Link>
          ) : (
            <span className="text-[11px] text-gray-400">A CRM admin can edit these prompts.</span>
          )}
          <button
            type="button"
            onClick={onClose}
            className="inline-flex min-h-[44px] items-center rounded-lg px-3 py-2 text-sm font-medium text-gray-600 transition-colors duration-150 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700 sm:min-h-0"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
